if (!customElements.get('ace-odp-category-browser')) {
  customElements.define('ace-odp-category-browser', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.input = this.querySelector('[data-odp-search]');
      this.catalog = this.querySelector('[data-odp-catalog]');
      this.status = this.querySelector('[data-odp-status]');
      this.active = this.querySelector('[data-odp-active]');
      this.activeLabel = this.querySelector('[data-odp-active-label]');
      this.empty = this.querySelector('[data-odp-empty]');
      if (!this.catalog) return;
      this.cards = this.hasAttribute('data-odp-cards');
      this.anchors = Array.from(this.querySelectorAll('[data-odp-hub-anchor]'));
      try { this.hubs = JSON.parse(this.querySelector('[data-odp-hubs]')?.textContent || '{}'); }
      catch (_) { this.hubs = {}; }
      this.groups = Array.from(this.querySelectorAll('[data-odp-group]')).map((element, index) => {
        const summary = element.querySelector('summary');
        const lists = element.querySelectorAll('.ace-odp-browser__children ul');
        if (this.cards && summary) {
          summary.id ||= `${this.id}-group-${index + 1}`;
          lists.forEach((list) => {
            list.setAttribute('tabindex', '0');
            list.setAttribute('aria-labelledby', summary.id);
          });
        }
        return {
          element, summary, id: element.dataset.odpGroup, title: element.dataset.odpTitle || '',
          rootIds: (element.dataset.odpCombinedRoots || element.dataset.odpGroup).split(',').map((id) => id.trim()).filter(Boolean),
          sources: Array.from(element.querySelectorAll('.ace-odp-browser__children[data-odp-source-root]')).map((source) => ({
            element: source, id: source.dataset.odpSourceRoot, title: source.dataset.odpSourceTitle || ''
          })),
          children: Array.from(element.querySelectorAll('[data-odp-child]')).map((child) => {
            const source = child.closest('[data-odp-source-root]');
            return {
              element: child, id: child.dataset.odpChild, title: child.dataset.odpTitle || '',
              rootId: child.dataset.odpParent || source?.dataset.odpSourceRoot || element.dataset.odpGroup,
              sourceTitle: source?.dataset.odpSourceTitle || element.dataset.odpTitle || ''
            };
          })
        };
      });
      this.scope = this.closest('[data-office-explorer]') || this;
      this.hubKey = this.hubs[this.hubKey] ? this.hubKey : null;
      this.searching = Boolean(this.searching);
      this.previousOpen ||= new Map();
      this.input?.addEventListener('input', () => {
        // A name search always covers the entire hierarchy, even after choosing a space.
        if (this.input.value.trim() && this.hubKey) {
          this.hubKey = null;
          this.clearHubHash();
        }
        this.filter();
      }, options);
      this.querySelector('[data-odp-clear]')?.addEventListener('click', () => {
        if (this.input) this.input.value = '';
        this.filter();
        this.input?.focus();
      }, options);
      this.querySelector('[data-odp-all]')?.addEventListener('click', () => {
        this.reset();
        history.replaceState(history.state, '', `#${this.id}`);
        this.input?.focus();
      }, options);
      this.addEventListener('toggle', (event) => {
        if (!this.cards || !event.target.open || !event.target.hasAttribute('data-odp-group')) return;
        this.groups.forEach(({ element }) => {
          if (element !== event.target) element.open = false;
        });
      }, { ...options, capture: true });
      this.scope.addEventListener('click', (event) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a');
        const href = link?.getAttribute('href');
        if (!href?.startsWith('#')) return;
        const anchor = this.anchors.find((item) => item.id === href.slice(1));
        if (anchor && this.hubs[anchor.dataset.odpHubAnchor]) {
          event.preventDefault();
          this.selectHub(anchor.dataset.odpHubAnchor);
          history.pushState(history.state, '', href);
          this.align(true);
        } else if (href === `#${this.id}`) {
          event.preventDefault();
          this.reset();
          history.pushState(history.state, '', href);
          this.align(true);
        }
      }, options);
      this.restore = () => {
        const anchor = this.anchors.find((item) => `#${item.id}` === location.hash);
        if (anchor && this.hubs[anchor.dataset.odpHubAnchor]) {
          this.selectHub(anchor.dataset.odpHubAnchor);
          this.align(false);
        } else if (location.hash === `#${this.id}` || this.hubKey) {
          this.reset();
        }
      };
      window.addEventListener('hashchange', this.restore, options);
      window.addEventListener('popstate', this.restore, options);
      this.classList.add('is-ready');
      this.classList.toggle('ace-odp-browser--cards', this.cards);
      this.filter({ preserveOpen: true });
      // Reconnecting a section preserves the current search and open disclosure.
      if (!this.hasConnected) this.restore();
      this.hasConnected = true;
    }
    normalize(value) {
      return value.normalize('NFKC').toLocaleLowerCase('ja').replace(/[ァ-ヶ]/g, (letter) => String.fromCharCode(letter.charCodeAt(0) - 0x60));
    }
    matchesQuery(value, tokens) {
      const text = this.normalize(value);
      return tokens.every((token) => text.includes(token));
    }
    clearHubHash() {
      if (this.anchors.some((anchor) => `#${anchor.id}` === location.hash)) {
        history.replaceState(history.state, '', `#${this.id}`);
      }
    }
    reset() {
      this.hubKey = null;
      if (this.input) this.input.value = '';
      this.filter();
      this.catalog.open = true;
    }
    selectHub(key) {
      if (!this.hubs[key]) return;
      this.hubKey = key;
      if (this.input) this.input.value = '';
      this.searching = false;
      this.previousOpen = new Map();
      this.groups.forEach(({ element }) => { element.open = false; });
      this.filter();
      this.catalog.open = true;
    }
    filter({ preserveOpen = false } = {}) {
      const query = this.normalize(this.input?.value.trim() || '');
      const tokens = query.split(/\s+/).filter(Boolean);
      if (query && !this.searching) this.previousOpen = new Map(this.groups.map(({ element }) => [element, element.open]));
      const restoreOpen = !query && this.searching;
      this.searching = Boolean(query);
      const hub = this.hubKey ? this.hubs[this.hubKey] : null;
      const roots = new Set(hub?.root_ids || []);
      const nodes = new Set(hub?.node_ids || []);
      let visibleCount = 0;
      this.filteredGroups = this.groups.filter((group) => {
        const rootAllowed = !hub || group.rootIds.some((id) => roots.has(id));
        const rootMatch = this.matchesQuery(group.title, tokens);
        let childrenShown = 0;
        group.children.forEach((child) => {
          // Combined home cards retain each series' official source root.
          const allowed = !hub || roots.has(child.rootId) || nodes.has(child.id);
          const shown = Boolean(allowed && (!query || rootMatch || this.matchesQuery(`${group.title} ${child.sourceTitle} ${child.title}`, tokens)));
          child.element.hidden = !shown;
          if (shown) childrenShown += 1;
        });
        let sourceRootsShown = 0;
        group.sources.forEach((source) => {
          const hasChildren = group.children.some((child) => child.rootId === source.id && !child.element.hidden);
          const allowed = !hub || roots.has(source.id);
          const matches = rootMatch || this.matchesQuery(`${group.title} ${source.title}`, tokens);
          source.element.hidden = !(hasChildren || (allowed && matches));
          if (!source.element.hidden) sourceRootsShown += 1;
        });
        const shown = group.sources.length ? sourceRootsShown > 0 : childrenShown > 0 || (rootAllowed && rootMatch);
        group.element.hidden = !shown;
        if (restoreOpen) group.element.open = this.previousOpen.get(group.element) || false;
        if (query && !this.cards) group.element.open = shown;
        if (this.cards && !shown) group.element.open = false;
        if (shown) visibleCount += childrenShown + (group.sources.length ? sourceRootsShown : 1);
        return shown;
      });
      if (this.cards && query && !preserveOpen) {
        this.groups.forEach((group) => { group.element.open = group === this.filteredGroups[0]; });
      }
      if (this.active) this.active.hidden = !hub;
      if (this.activeLabel) this.activeLabel.textContent = hub ? (this.anchors.find((item) => item.dataset.odpHubAnchor === this.hubKey)?.dataset.odpHubLabel || '') : '';
      if (this.empty) this.empty.hidden = this.filteredGroups.length > 0;
      if (this.status) this.status.textContent = query || hub ? (this.dataset.statusTemplate || '__COUNT__').replace('__COUNT__', String(visibleCount)) : this.dataset.defaultStatus;
      if (query || hub || this.cards) this.catalog.open = true;
    }
    align(focus) {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const nodes = ['.ace-member-announcement-section', '.section-header', '.ace-category-navigation-section'].map((selector) => document.querySelector(selector));
        const edge = Math.max(0, ...nodes.filter(Boolean).map((node) => node.getBoundingClientRect().bottom));
        window.scrollTo({ top: Math.max(0, this.getBoundingClientRect().top + scrollY - edge - 12), behavior: 'instant' });
        if (focus) {
          const heading = this.querySelector('h3');
          if (!heading) return;
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
          heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
        }
      });
    }
    disconnectedCallback() {
      this.controller?.abort();
      cancelAnimationFrame(this.frame);
    }
  });
}
